import React, { useEffect, useMemo, useRef } from "react";
import { Button, DatePicker, DeleteIcon, TextField } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { Controller, useWatch } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { useDealersForAssignment } from "~/components/Forms/UsersForm/hooks/useDealersForAssignment";
import { useAvailableRegionalManagers } from "~/components/Forms/UsersForm/hooks/useRegionalManagerList";
import { IAssignmentRow } from "~/components/Forms/UsersForm/TemporaryAssignments/model/utils";
import { Select } from "~/shared/ui/Select";

import s from "./TemporaryAssignments.less";

interface IAssignmentRowProps {
  index: number;
  control: any;
  errors?: any;
  userId: number;
  // Permanent-дилеры редактируемого пользователя — для исключения пересечений.
  permanentDealers?: { label: string; value: number }[];
  // Дилеры, уже назначенные временно другим пользователям.
  // PASDILER-2078: исключение из выбора временно отключено (см. dealerOptions
  // и авто-заполнение ниже) — бэк должен вернуть ошибку о дубле.
  externallyAssignedDealerIds?: Set<number>;
  // Все строки временных закреплений — для построения alreadySetDealerIds /
  // alreadySetRmIds (исключая текущую, чтобы можно было редактировать строку
  // без само-блокировки).
  allAssignments?: IAssignmentRow[];
  onRemove: () => void;
}

const AssignmentRow: React.FC<IAssignmentRowProps> = ({
  index,
  control,
  errors,
  userId,
  permanentDealers,
  externallyAssignedDealerIds,
  allAssignments,
  onRemove,
}) => {
  const { t } = useTranslation();
  const rmPrefix = `assignments[${index}]`;

  // alreadySetDealerIds — дилеры, уже выбранные в других строках (кроме
  // текущей). Используются для исключения дублей при выборе/авто-заполнении.
  const alreadySetDealerIds = useMemo(() => {
    const ids = new Set<number>();
    (allAssignments ?? []).forEach((row, i) => {
      if (i === index) return;
      (row.dealers ?? []).forEach((id) => ids.add(id));
    });
    return ids;
  }, [allAssignments, index]);

  // alreadySetRmIds — РМ, уже выбранные в других строках (кроме текущей):
  // один РМ не должен фигурировать в двух временных закреплениях.
  const alreadySetRmIds = useMemo(() => {
    const ids = new Set<number>();
    (allAssignments ?? []).forEach((row, i) => {
      if (i === index) return;
      if (row.permanentRmId != null) ids.add(row.permanentRmId);
    });
    return ids;
  }, [allAssignments, index]);

  // Permanent-дилеры пользователя — исключаем из временных закреплений:
  // дилер не может быть одновременно закреплён постоянно и временно.
  const permanentDealerIds = useMemo(
    () => new Set((permanentDealers ?? []).map((d) => d.value)),
    [permanentDealers],
  );

  const {
    items,
    isLoading: rmLoading,
    onSearch,
    onPopupScroll,
  } = useAvailableRegionalManagers(userId);

  // РМ строки — permanentRmId в терминах API: ОТНОСИТЕЛЬНО кого назначаем
  // (чьи дилеры временно передаются редактируемому пользователю).
  const permanentRmId = useWatch({
    control,
    name: `${rmPrefix}.permanentRmId`,
  }) as number | null | undefined;
  const permanentRmName = useWatch({
    control,
    name: `${rmPrefix}.permanentRmName`,
  }) as string | null | undefined;

  const rmOptions = useMemo(() => {
    // Исключаем редактируемого пользователя и РМ, уже выбранных в других
    // строках (кроме значения текущей строки).
    const opts = items
      .filter(
        (rm) =>
          rm.id !== userId &&
          (rm.id === permanentRmId || !alreadySetRmIds.has(rm.id)),
      )
      .map((rm) => ({ label: rm.fullname, value: rm.id }));
    // Если текущий permanentRmId не попал в available-managers (пагинация/
    // фильтр), добавляем опцию из серверного permanentRmName, чтобы Select
    // показал имя. Самого редактируемого пользователя не возвращаем.
    if (
      permanentRmId != null &&
      permanentRmId !== userId &&
      !opts.some((o) => o.value === permanentRmId) &&
      permanentRmName
    ) {
      opts.push({ label: permanentRmName, value: permanentRmId });
    }
    return opts;
  }, [items, permanentRmId, permanentRmName, userId, alreadySetRmIds]);

  const selectedRm = useMemo(
    () => items.find((rm) => rm.id === permanentRmId) || null,
    [items, permanentRmId],
  );

  // Логин РМ — из available-managers, с fallback на серверное поле строки
  // (когда РМ не пришёл в первой странице/отфильтрован).
  const rowRmLogin = useWatch({
    control,
    name: `${rmPrefix}.permanentRmLogin`,
  }) as string | null | undefined;
  const rmLogin = selectedRm?.login ?? rowRmLogin ?? "";

  // Направления деятельности РМ — аналогично с fallback на permanentRmDirections.
  const rowRmDirections = useWatch({
    control,
    name: `${rmPrefix}.permanentRmDirections`,
  }) as string[] | undefined;
  const rmDirections = useMemo(
    () =>
      (selectedRm?.directions ?? []).map((d) => d.name).length > 0
        ? (selectedRm?.directions ?? []).map((d) => d.name)
        : (rowRmDirections ?? []),
    [selectedRm, rowRmDirections],
  );

  // Текущие выбранные дилеры в этой строке — их оставляем видимыми,
  // даже если они есть в alreadySetDealerIds (иначе выбранные теги исчезнут).
  const rowDealers = useWatch({ control, name: `${rmPrefix}.dealers` }) as
    | number[]
    | undefined;
  const rowDealerSet = new Set(rowDealers ?? []);

  // Источник дилеров — GET /dealer/dealers. Если выбран РМ — фильтр по regmenId
  // (дилеры того РМ, относительно которого назначаем), иначе — все дилеры.
  // serverDealers — fallback для имён уже выбранных тегов, когда они не пришли
  // в первой странице /dealer/dealers.
  const {
    items: dealerItems,
    isLoading: dealersLoading,
    isFetching: dealersFetching,
    onSearch: onSearchDealers,
    onPopupScroll: onDealersScroll,
  } = useDealersForAssignment(permanentRmId ?? undefined);

  const serverDealers = useWatch({
    control,
    name: `${rmPrefix}.serverDealers`,
  }) as { id: number; name: string }[] | undefined;

  // options: дилеры из /dealer/dealers (исключая alreadySet, permanent-дилеры
  // пользователя и занятые временно другими, кроме уже выбранных в этой строке)
  // + серверные для отображения имён выбранных тегов.
  const dealerOptions = useMemo(() => {
    const options = dealerItems
      .filter(
        (d) =>
          rowDealerSet.has(d.id) ||
          (!alreadySetDealerIds.has(d.id) && !permanentDealerIds.has(d.id)),
        // PASDILER-2078: фильтр «уже назначен временно другому пользователю»
        // временно отключён, чтобы бэк возвращал ошибку о дубле. Для возврата
        // добавить в условие выше: !externallyAssignedDealerIds?.has(d.id)
      )
      .map((d) => ({
        label: d.dealerName ?? d.name ?? String(d.id),
        value: d.id,
      }));
    if (serverDealers && serverDealers.length > 0) {
      const present = new Set(options.map((o) => o.value));
      serverDealers.forEach((d) => {
        if (!present.has(d.id) && rowDealerSet.has(d.id))
          options.push({ label: d.name, value: d.id });
      });
    }
    return options;
  }, [
    dealerItems,
    alreadySetDealerIds,
    permanentDealerIds,
    externallyAssignedDealerIds,
    rowDealers,
    serverDealers,
  ]);

  const prevRegmenIdRef = useRef<number | null | undefined>(undefined);
  const isFirstRmRenderRef = useRef(true);
  // Ждём, пока useDealersForAssignment подгрузит дилеров нового РМ.
  const pendingAutofillRef = useRef(false);

  useEffect(() => {
    if (isFirstRmRenderRef.current) {
      isFirstRmRenderRef.current = false;
      prevRegmenIdRef.current = permanentRmId;
      return;
    }
    const prev = prevRegmenIdRef.current;
    prevRegmenIdRef.current = permanentRmId;
    if (prev === permanentRmId) return; // нет смены

    if (!permanentRmId) return; // сброс РМ дилеров не сбрасывает
    pendingAutofillRef.current = true;
  }, [permanentRmId]);

  useEffect(() => {
    if (!pendingAutofillRef.current) return;
    if (dealersLoading || dealersFetching || dealerItems.length === 0) return;
    pendingAutofillRef.current = false;
    const ids = dealerItems
      .map((d) => d.id)
      .filter(
        (id) => !alreadySetDealerIds.has(id) && !permanentDealerIds.has(id),
      );
    control.setValue(`${rmPrefix}.dealers`, ids);
  }, [
    dealerItems,
    dealersLoading,
    dealersFetching,
    alreadySetDealerIds,
    permanentDealerIds,
  ]);

  return (
    <div className={s.assignmentGroup}>
      <div className={s.groupHeader}>
        <Button
          variant="text"
          color="secondary"
          size="small"
          htmlType="button"
          icon={<DeleteIcon size={16} />}
          aria-label={t("ui.delete")}
          title={t("ui.delete")}
          className={s.removeButton}
          onClick={onRemove}
        />
        <span className={s.groupTitle}>
          {t("users.assignment_number", { number: index + 1 })}
        </span>
      </div>
      <Flex gap={16} align="flex-start">
        <div className={s.field}>
          <span className={s.fieldLabel}>{t("users.assignment_period")}</span>
          <div className={s.periodInputs}>
            <Controller
              name={`${rmPrefix}.startDate`}
              control={control}
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("ui.from_date")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
            <span className={s.periodDash}>–</span>
            <Controller
              name={`${rmPrefix}.endDate`}
              control={control}
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("ui.to_date")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
          </div>
        </div>
        <div className={s.field}>
          <span className={s.fieldLabel}>{t("users.regional_manager")}</span>
          <Controller
            name={`${rmPrefix}.permanentRmId`}
            control={control}
            render={({ onChange, value }) => (
              <>
                <Select
                  value={value}
                  onChange={(val) => {
                    // «Не выбран» — сентинел 0 (опция с value=null antd глотает).
                    const next = val === 0 ? null : val;
                    onChange(next);
                    // Сброс РМ: чистим подписи РМ,
                    // дилеров НЕ трогаем (даже унаследованных от другого РМ).
                    if (next == null) {
                      control.setValue(`${rmPrefix}.permanentRmName`, null);
                      control.setValue(`${rmPrefix}.permanentRmLogin`, null);
                    }
                  }}
                  options={[
                    { label: t("users.rm_not_selected"), value: 0 },
                    ...rmOptions,
                  ]}
                  showSearch
                  filterOption={false}
                  onSearch={onSearch}
                  onPopupScroll={onPopupScroll}
                  loading={rmLoading}
                  placeholder={t("users.select_rm")}
                  status={errors?.permanentRmId ? "error" : undefined}
                  style={{ width: "100%" }}
                />
                {errors?.permanentRmId?.message && (
                  <span className={s.fieldError}>
                    {errors.permanentRmId.message}
                  </span>
                )}
              </>
            )}
          />
        </div>
      </Flex>
      <Flex gap={16} align="flex-start">
        <div className={s.field}>
          <span className={s.fieldLabel}>{t("users.rm_login")}</span>
          <TextField value={rmLogin} ellipsis />
        </div>
        <div className={s.field}>
          <span className={s.fieldLabel}>{t("users.activity_direction")}</span>
          <TextField value={rmDirections} ellipsis />
        </div>
        <div className={`${s.field} ${s.fieldWide}`}>
          <span className={s.fieldLabel}>{t("users.allDC")}</span>
          <Controller
            name={`${rmPrefix}.dealers`}
            control={control}
            render={({ onChange, value }) => (
              <>
                <Select
                  mode="multiple"
                  value={value ?? []}
                  onChange={onChange}
                  options={dealerOptions}
                  maxTagCount="responsive"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={onSearchDealers}
                  onPopupScroll={onDealersScroll}
                  loading={dealersLoading}
                  placeholder={t("users.select_dealers")}
                  status={errors?.dealers ? "error" : undefined}
                  style={{ width: "100%" }}
                />
                {errors?.dealers?.message && (
                  <span className={s.fieldError}>{errors.dealers.message}</span>
                )}
              </>
            )}
          />
        </div>
      </Flex>
    </div>
  );
};

export default AssignmentRow;
